import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';

function LandingPage() {
  const navigate = useNavigate();
  const [showSplash, setShowSplash] = useState(true);
  const [animationPhase, setAnimationPhase] = useState(0);
  const [activeService, setActiveService] = useState(0);

  const services = [
    { icon: '📚', title: 'Tutoring', desc: 'Learn from peer experts', color: '#667eea' },
    { icon: '👥', title: 'Community', desc: 'Connect with students', color: '#ec4899' },
    { icon: '🛒', title: 'Marketplace', desc: 'Buy & sell with students', color: '#f97316' },
    { icon: '🏠', title: 'Rentals', desc: 'Find what you need', color: '#22c55e' },
    { icon: '🧠', title: 'AI Tools', desc: 'Study smarter, not harder', color: '#8b5cf6' },
    { icon: '🎯', title: 'Services', desc: 'Skills on demand', color: '#06b6d4' },
  ];

  useEffect(() => {
    const timer1 = setTimeout(() => setAnimationPhase(1), 500);
    const timer2 = setTimeout(() => setAnimationPhase(2), 1200);
    const timer3 = setTimeout(() => setAnimationPhase(3), 2000);
    const timer4 = setTimeout(() => setShowSplash(false), 2800);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, []);

  useEffect(() => {
    if (!showSplash) {
      const interval = setInterval(() => {
        setActiveService((prev) => (prev + 1) % services.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [showSplash, services.length]);

  if (showSplash) {
    return (
      <div className="splash-screen">
        <div className={`splash-content phase-${animationPhase}`}>
          <div className="splash-logo">
            <div className="logo-circle">
              <span className="logo-icon">⚡</span>
            </div>
            <div className="logo-rings">
              <div className="ring ring-1"></div>
              <div className="ring ring-2"></div>
              <div className="ring ring-3"></div>
            </div>
          </div>
          <h1 className="splash-title">
            <span className="title-on">ON</span>
            <span className="title-tap">TAP</span>
          </h1>
          <p className="splash-tagline">Everything you need. One tap away.</p>
          <div className="splash-loader">
            <div className="loader-bar"></div>
          </div>
        </div>
        <div className="splash-particles">
          {[...Array(20)].map((_, i) => (
            <div key={i} className={`particle particle-${i + 1}`}></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="landing-page-pro">
      {/* Navigation */}
      <nav className="pro-nav">
        <div className="pro-nav-inner">
          <div className="pro-logo">
            <span className="pro-logo-icon">⚡</span>
            <span className="pro-logo-text">ONTAP</span>
          </div>
          <div className="pro-nav-links">
            <button onClick={() => navigate('/showcase')}>Features</button>
            <button onClick={() => navigate('/demo')}>Demo</button>
            <button onClick={() => navigate('/')}>Explore</button>
          </div>
          <div className="pro-nav-actions">
            <button className="pro-btn-ghost" onClick={() => navigate('/login')}>
              Sign In
            </button>
            <button className="pro-btn-primary" onClick={() => navigate('/register')}>
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pro-hero">
        <div className="pro-hero-inner">
          {/* Left - Phone */}
          <div className="pro-phone-container">
            <div className="pro-phone">
              <div className="pro-phone-notch"></div>
              <div className="pro-phone-screen">
                <div className="pro-screen-header">
                  <span className="pro-screen-location">📍 Your University</span>
                  <span className="pro-screen-time">9:41</span>
                </div>
                <div className="pro-screen-greeting">Good morning! 👋</div>

                {/* Active Service Card */}
                <div
                  className="pro-active-card"
                  style={{
                    background: `linear-gradient(135deg, ${services[activeService].color}30, ${services[activeService].color}10)`,
                    borderColor: services[activeService].color
                  }}
                >
                  <span className="pro-active-icon">{services[activeService].icon}</span>
                  <div className="pro-active-info">
                    <span className="pro-active-title">{services[activeService].title}</span>
                    <span className="pro-active-desc">{services[activeService].desc}</span>
                  </div>
                  <span className="pro-active-arrow">→</span>
                </div>

                {/* Service Grid */}
                <div className="pro-service-grid">
                  {services.map((s, i) => (
                    <div
                      key={i}
                      className={`pro-service-item ${i === activeService ? 'active' : ''}`}
                      style={{
                        background: s.color + '20',
                        borderColor: i === activeService ? s.color : 'transparent'
                      }}
                    >
                      <span className="pro-service-emoji">{s.icon}</span>
                      <span className="pro-service-name">{s.title}</span>
                    </div>
                  ))}
                </div>

                <div className="pro-promo-banner">
                  <span>🎉 Join 1000+ students today!</span>
                </div>
              </div>
            </div>
            <div className="pro-phone-shadow"></div>
          </div>

          {/* Right - Content */}
          <div className="pro-hero-content">
            <div className="pro-badge">
              <span>🎓 Built for International Students</span>
            </div>
            <h1 className="pro-title">
              Campus Life,
              <br />
              <span className="pro-gradient">Simplified.</span>
            </h1>
            <p className="pro-subtitle">
              The all-in-one super app for international students across USA & Canada.
              Tutoring, marketplace, rentals, and AI tools — everything you need in one place.
            </p>
            <div className="pro-cta-group">
              <button className="pro-cta-primary" onClick={() => navigate('/')}>
                <span>Get Started</span>
                <span className="pro-cta-icon">→</span>
              </button>
              <button className="pro-cta-secondary" onClick={() => navigate('/demo')}>
                <span className="pro-play">▶</span>
                <span>Watch Demo</span>
              </button>
            </div>
            <div className="pro-trust">
              <div className="pro-trust-avatars">
                <div className="pro-avatar">👨‍🎓</div>
                <div className="pro-avatar">👩‍🎓</div>
                <div className="pro-avatar">🧑‍🎓</div>
                <div className="pro-avatar">+</div>
              </div>
              <span className="pro-trust-text">Trusted by <strong>1000+</strong> students</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Strip */}
      <section className="pro-features-strip">
        <div className="pro-features-inner">
          {services.map((service, i) => (
            <div key={i} className="pro-feature-chip">
              <span>{service.icon}</span>
              <span>{service.title}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="pro-footer">
        <div className="pro-footer-inner">
          <div className="pro-footer-brand">
            <span>⚡ ONTAP Global</span>
            <p>Built for International Students</p>
          </div>
          <div className="pro-footer-links">
            <button onClick={() => navigate('/showcase')}>Showcase</button>
            <button onClick={() => navigate('/demo')}>Demo</button>
            <button onClick={() => navigate('/login')}>Sign In</button>
          </div>
        </div>
        <div className="pro-footer-bottom">
          <p>© 2025 ONTAP Global • USA & Canada Universities</p>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
